import {
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type {
  AwsVpcConnectivityConfig,
  ResourceTypeToSecurityGroup,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { BottomDialog } from '@/components/dialogs/bottomDialog';
import { PanelFooter } from '@/components/panels/panelFooter';
import { PanelHeader } from '@/components/panels/panelHeader';
import { AWSSubnetSelect } from '@/components/selectors/awsSubnetSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { docsBaseUrl } from '@/contexts/environment';

import {
  cloudAccountConfigurationAwsResourceTypes,
  cloudAccountConfigurationAwsResourceTypesTitles,
} from './types';

import { ReadMoreLink } from '../../shared/readMoreLink';

export const AwsCustomizeNetworkBottomDialog = ({
  accountId,
  config: initialConfig,
  availabilityZones,
  onSave,
  onCancel,
}: {
  accountId: string;
  config: AwsVpcConnectivityConfig | undefined;
  availabilityZones: string[];
  onSave: (config: AwsVpcConnectivityConfig) => void;
  onCancel: () => void;
}) => {
  const [config, setConfig] = useState<AwsVpcConnectivityConfig | undefined>(
    initialConfig ? { ...initialConfig } : undefined
  );

  const [
    securityGroupsKeysCustomizedState,
    setSecurityGroupsKeysCustomizedState,
  ] = useState<Record<string, boolean>>(
    Object.fromEntries(
      cloudAccountConfigurationAwsResourceTypes.map((key) => [
        key,
        !!initialConfig?.securityGroups?.[key]?.length,
      ])
    )
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConfig(initialConfig ? { ...initialConfig } : undefined);
    setSecurityGroupsKeysCustomizedState(
      Object.fromEntries(
        cloudAccountConfigurationAwsResourceTypes.map((key) => [
          key,
          !!initialConfig?.securityGroups?.[key]?.length,
        ])
      )
    );
  }, [initialConfig]);

  const submitDisabled = () => {
    if (!config) {
      return true;
    }

    if (
      !config.subnetsPerAvailabilityZone?.length &&
      !Object.keys(securityGroupsKeysCustomizedState).some(
        (key) =>
          !!config.securityGroups?.[key as keyof ResourceTypeToSecurityGroup]
            ?.length
      )
    ) {
      return true;
    }

    if (
      config.subnetsPerAvailabilityZone?.some(
        (x) => !x.availabilityZone || !x.subnetId
      )
    ) {
      return true;
    }

    return Object.entries(securityGroupsKeysCustomizedState).some(
      ([key, value]) => {
        if (value) {
          const securityGroupIds =
            config.securityGroups?.[key as keyof ResourceTypeToSecurityGroup] ||
            [];

          if (!securityGroupIds.length) {
            return true;
          }
        }
        return false;
      }
    );
  };

  const onSaveClick = () => {
    if (config) {
      onSave({
        ...config,
        subnetsPerAvailabilityZone: config.subnetsPerAvailabilityZone?.filter(
          (x) => x.availabilityZone && x.subnetId
        ),
      });
    }
  };

  return (
    <BottomDialog open={!!config}>
      <Stack className='h-full'>
        <PanelHeader
          onClose={onCancel}
          title='Customize VPC'
          subtitle={
            <Stack className='mx-[84px] my-[24px]' gap='24px' flexGrow={1}>
              <Stack direction='row' gap='4px'>
                <Typography component='span' fontWeight={300}>
                  Customizing settings for
                </Typography>
                <Typography component='span' className='font-semibold'>
                  {`${config?.vpc}.`}
                </Typography>
              </Stack>
              <Typography
                className='w-[700px]'
                fontWeight={300}
                lineHeight={'20px'}
              >
                {`Define the subnets and security groups you want Eon to use. `}
                <ReadMoreLink
                  href={`${docsBaseUrl}/user-guide/connection/aws/restore-accounts/aws-customize-restore-server-configuration`}
                />
              </Typography>
            </Stack>
          }
        />
        <Stack
          gap='12px'
          className='py-[16px] px-[24px] overflow-y-auto'
          flexGrow={1}
        >
          <Stack className='mx-[60px] mt-[24px]' gap='24px' flexGrow={1}>
            <Stack direction='row' gap='100px'>
              <Stack gap='36px' className='w-[240px] pt-[8px]'>
                <Typography className='font-semibold'>
                  Subnets per availability zone
                </Typography>
                <Typography lineHeight={'20px'} fontWeight={300}>
                  {`The restore server will be launched in the subnets you specify here. Make sure outbound access is open in the selected subnets.`}
                </Typography>
              </Stack>
              <Stack
                flexGrow={1}
                gap='24px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                {availabilityZones.sort().map((az) => {
                  const subnetId = config?.subnetsPerAvailabilityZone?.find(
                    (y) => y.availabilityZone === az
                  )?.subnetId;

                  return (
                    <Stack
                      key={az}
                      direction='row'
                      alignItems='center'
                      gap='40px'
                      className='h-[33px]'
                    >
                      <Typography className='w-[150px]'>{az}</Typography>
                      <FormControlLabel
                        labelPlacement='start'
                        control={<Switch className='ml-[12px]' />}
                        label='Customize'
                        checked={subnetId !== undefined}
                        onChange={(event, checked) => {
                          setConfig((prev) => {
                            if (!prev) {
                              return prev;
                            }

                            const newState: AwsVpcConnectivityConfig = {
                              ...prev,
                              subnetsPerAvailabilityZone:
                                prev.subnetsPerAvailabilityZone?.filter(
                                  (y) => y.availabilityZone !== az
                                ),
                            };

                            if (checked) {
                              if (!newState.subnetsPerAvailabilityZone) {
                                newState.subnetsPerAvailabilityZone = [];
                              }

                              newState.subnetsPerAvailabilityZone?.push({
                                availabilityZone: az,
                                subnetId: '',
                              });
                            }

                            return newState;
                          });
                        }}
                      />
                      <Typography
                        color={
                          subnetId === undefined
                            ? 'var(--mui-palette-text-disabled)'
                            : undefined
                        }
                        width={'180px'}
                        textAlign={'right'}
                      >
                        {subnetId === undefined
                          ? 'Use default subnet'
                          : 'Use subnet:'}
                      </Typography>
                      {subnetId !== undefined && (
                        <Stack className='w-[250px]'>
                          <AWSSubnetSelect
                            hideLabel
                            accountId={accountId}
                            regionName={config?.region}
                            subnetId={subnetId}
                            availabilityZoneScope={az}
                            vpcScope={config?.vpc || ''}
                            onChange={(subnet) => {
                              setConfig((prev) => {
                                if (!prev || !subnet) {
                                  return prev;
                                }

                                const newState: AwsVpcConnectivityConfig = {
                                  ...prev,
                                  subnetsPerAvailabilityZone:
                                    prev.subnetsPerAvailabilityZone?.map(
                                      (y) => {
                                        if (y.availabilityZone === az) {
                                          return {
                                            ...y,
                                            subnetId: subnet,
                                          };
                                        }

                                        return y;
                                      }
                                    ),
                                };

                                return newState;
                              });
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
            <Divider className='my-[20px]' />
            <Stack direction='row' gap='100px'>
              <Stack gap='36px' className='w-[240px] pt-[8px]'>
                <Typography className='font-semibold'>
                  Security groups per resource type
                </Typography>
                <Typography lineHeight={'20px'} fontWeight={300}>
                  {`The restore server and restored RDS instance will use the specified security groups. Make sure outbound access is open, and that the RDS and restore server security groups are open to each other.`}
                </Typography>
              </Stack>
              <Stack
                flexGrow={1}
                gap='24px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                {cloudAccountConfigurationAwsResourceTypes.map((key) => {
                  const securityGroupIds = config?.securityGroups?.[key] || [];
                  const title =
                    cloudAccountConfigurationAwsResourceTypesTitles[key];

                  return (
                    <Stack
                      key={key}
                      direction='row'
                      alignItems='center'
                      gap='40px'
                      className='h-[33px]'
                    >
                      <Typography className='w-[150px]'>{title}</Typography>
                      <FormControlLabel
                        labelPlacement='start'
                        control={<Switch className='ml-[12px]' />}
                        label='Customize'
                        checked={securityGroupsKeysCustomizedState[key]}
                        onChange={(event, checked) => {
                          setConfig((prev) => {
                            if (!prev) {
                              return prev;
                            }

                            setSecurityGroupsKeysCustomizedState({
                              ...securityGroupsKeysCustomizedState,
                              [key]: checked,
                            });

                            if (!checked) {
                              return {
                                ...prev,
                                securityGroups: {
                                  ...prev.securityGroups,
                                  key: [],
                                },
                              };
                            }

                            return prev;
                          });
                        }}
                      />
                      <Typography
                        color={
                          securityGroupsKeysCustomizedState[key]
                            ? undefined
                            : 'var(--mui-palette-text-disabled)'
                        }
                        width={'180px'}
                        textAlign={'right'}
                      >
                        {securityGroupsKeysCustomizedState[key]
                          ? 'Use security group:'
                          : 'Use default security group'}
                      </Typography>
                      {securityGroupsKeysCustomizedState[key] && (
                        <Stack className='w-[250px]'>
                          <SecurityGroupSelect
                            isOptionalField
                            hideLabel
                            accountId={accountId}
                            regionName={config?.region}
                            vpc={config?.vpc || ''}
                            securityGroupIds={securityGroupIds}
                            onChange={(securityGroupIds) => {
                              setConfig((prev) => {
                                if (!prev || !securityGroupIds) {
                                  return prev;
                                }

                                const newState: AwsVpcConnectivityConfig = {
                                  ...prev,
                                  securityGroups: {
                                    ...prev.securityGroups,
                                    [key]: securityGroupIds,
                                  },
                                };

                                return newState;
                              });
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <PanelFooter
          primaryButton={{
            text: 'Save Settings',
            onClick: onSaveClick,
            disabled: submitDisabled(),
            'data-testid': 'save-custom-vpc',
          }}
          secondaryButton={{
            text: 'Cancel',
            onClick: onCancel,
            'data-testid': 'cancel-custom-vpc',
          }}
        />
      </Stack>
    </BottomDialog>
  );
};
